/*
 * Axis helpers recharts doesn't provide, used alongside the shadcn chart primitives.
 *
 * We generate the ticks ourselves so the unit can sit on the top tick alone.
 */

export interface ChartScale {
  divisor: number;
  unit?: string;
}

export interface AxisTickOptions {
  /** Force zero into the range -- bars are read by length, so their axis needs it. */
  baselineAtZero?: boolean;
}

/** Recharts' own default when there is nothing finite to plot. */
const EMPTY_TICKS = [0, 0.25, 0.5, 0.75, 1];

const TARGET_INTERVALS = 4;

/** The largest absolute value in a column, or 0 when it holds nothing plottable. */
export function columnMax (rows: Record<string, any>[], column: string): number {
  let largest = 0;

  for (const row of rows) {
    const value = Number(row[column]);

    if (Number.isFinite(value)) {
      largest = Math.max(largest, Math.abs(value));
    }
  }

  return largest;
}

/** Rounds a rough interval up to the nearest 1, 2, 5 or 10 x 10^n, so labels stay round. */
function niceStep (roughStep: number): number {
  const powerOfTen = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const leadingDigit = roughStep / powerOfTen;

  if (leadingDigit <= 1) return powerOfTen;
  if (leadingDigit <= 2) return 2 * powerOfTen;
  if (leadingDigit <= 5) return 5 * powerOfTen;

  return 10 * powerOfTen;
}

export function axisTicks (
  rows: Record<string, any>[],
  columns: string[],
  { baselineAtZero = false }: AxisTickOptions = {}
): number[] {
  let lowest = Infinity;
  let highest = -Infinity;

  for (const row of rows) {
    for (const column of columns) {
      const value = Number(row[column]);

      if (Number.isFinite(value)) {
        lowest = Math.min(lowest, value);
        highest = Math.max(highest, value);
      }
    }
  }

  if (lowest === Infinity) return [...EMPTY_TICKS];

  if (baselineAtZero || lowest >= 0) {
    lowest = Math.min(0, lowest);
    highest = Math.max(0, highest);
  }

  /* A flat series has no range to divide, so give it one centred on its value. */
  if (lowest === highest) {
    if (lowest === 0) return [...EMPTY_TICKS];

    const padding = Math.abs(lowest) / 2;
    lowest -= padding;
    highest += padding;
  }

  const step = niceStep((highest - lowest) / TARGET_INTERVALS);

  /* Round the bounds out to whole steps so every tick is a round number. */
  const firstTick = Math.floor(lowest / step) * step;
  const lastTick = Math.ceil(highest / step) * step;

  const ticks: number[] = [];
  for (let tick = firstTick; tick < lastTick + step / 2; tick += step) {
    /* Re-round each step: repeated addition of e.g. 0.1 drifts into 0.30000000000004. */
    ticks.push(Number(tick.toFixed(10)));
  }

  return ticks;
}

/* Ticks, a domain pinned to them, and a formatter putting the unit on the top tick. */
export function axisProps (
  rows: Record<string, any>[],
  columns: string[],
  unit?: string,
  options: AxisTickOptions = {}
) {
  const ticks = axisTicks(rows, columns, options);
  const topTick = ticks[ticks.length - 1];

  return {
    ticks,
    domain: [ticks[0], topTick],
    tickFormatter: unit
      ? (value: number) => (value === topTick ? `${value} ${unit}` : String(value))
      : undefined,
  };
}

/* Rescales to a larger unit once values get big (kWh -> MWh -> GWh). Divides the data
 * through rather than the labels, which keeps gridlines and bar heights honest. */
export function scaleSeries (
  rows: Record<string, any>[],
  scaleByColumn: Record<string, (max: number) => ChartScale | null>
) {
  const scales: Record<string, ChartScale> = {};
  const units: Record<string, string | undefined> = {};
  const rescaledColumns: string[] = [];

  for (const [column, chooseScale] of Object.entries(scaleByColumn)) {
    const scale = chooseScale(columnMax(rows, column)) ?? { divisor: 1 };

    scales[column] = scale;
    units[column] = scale.unit;

    if (scale.divisor !== 1) rescaledColumns.push(column);
  }

  if (!rescaledColumns.length) return { data: rows, units };

  const data = rows.map((row) => {
    const scaledRow = { ...row };

    for (const column of rescaledColumns) {
      /* null means "no reading", which is not the same as zero -- leave it be. */
      if (row[column] == null) continue;

      const value = Number(row[column]);
      if (Number.isFinite(value)) scaledRow[column] = value / scales[column].divisor;
    }

    return scaledRow;
  });

  return { data, units };
}
